import { DEFAULT_DATE_FORMAT } from '@/app-shell/constants';
import { IS_CONSOLIDATED_SESSIONS_LIST_ENABLED } from '@/app-shell/flags/session-flags';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { getDateFnsLocale } from '@/utils/dates';
import { TZDate } from '@date-fns/tz';
import { sessionDateDisplayMessages } from '@heidi/session';
import { convertMomentToDateFnsFormat, parseTimeStamp } from '@heidi/utils';
import { getTimezone } from '@heidi/utils/get-country';
import { differenceInDays, format, startOfDay } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

export type DisplayFormattedDateOptions = {
  /** Anchor the calendar day in UTC when formatting (e.g. billing trial end dates). */
  calendarTimeZone?: 'UTC';
};

const isEnglishLocale = (locale: string): boolean =>
  locale.toLowerCase().startsWith('en');

const isArabicLocale = (locale: string): boolean => locale.toLowerCase().startsWith('ar');

const formatDateWithUserPreference = (
  date: Date,
  dateFormat: string,
  intl: IntlShape
): string =>
  isEnglishLocale(intl.locale)
    ? format(date, dateFormat)
    : format(date, dateFormat, { locale: getDateFnsLocale(intl.locale) });

const formatSessionTimeOfDay = (date: Date, intl: IntlShape): string => {
  if (isEnglishLocale(intl.locale)) {
    const timeFormat = IS_CONSOLIDATED_SESSIONS_LIST_ENABLED() ? 'hh:mma' : 'h:mma';
    return format(date, timeFormat);
  }

  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };
  if (isArabicLocale(intl.locale)) {
    options.numberingSystem = 'latn';
  }
  return intl.formatTime(date, options);
};

export const useSessionDateDisplay = () => {
  const intl = useIntl();
  const { data: userData } = useQueryUser((data) => ({
    session_settings: data?.session_settings,
  }));

  const dateFormat = useMemo(() => {
    const rawDateFormat = userData?.session_settings?.date_format || DEFAULT_DATE_FORMAT;
    return convertMomentToDateFnsFormat(rawDateFormat);
  }, [userData]);

  const displayDayRelative = useCallback(
    (
      datetime?: string | null,
      isUtc: boolean = false,
      fallbackDayOfWeek: boolean = false
    ) => {
      const today = startOfDay(new TZDate(new Date(), isUtc ? 'UTC' : getTimezone()));
      const sessionDay = parseTimeStamp(datetime, isUtc);
      const sessionDayStartOfDay = startOfDay(sessionDay);
      const diffDays = differenceInDays(today, sessionDayStartOfDay);
      const dayOfWeek = isEnglishLocale(intl.locale)
        ? format(sessionDay, 'EEEE')
        : format(sessionDay, 'EEEE', { locale: getDateFnsLocale(intl.locale) });

      if (diffDays === -1) {
        return intl.formatMessage(sessionDateDisplayMessages.tomorrow);
      }
      if (diffDays === 0) {
        return intl.formatMessage(sessionDateDisplayMessages.today);
      }
      if (diffDays === 1) {
        return intl.formatMessage(sessionDateDisplayMessages.yesterday);
      }
      if (diffDays > 1 && diffDays < 7) {
        return intl.formatMessage(sessionDateDisplayMessages.xDaysAgo, {
          count: diffDays,
        });
      }
      if (diffDays === 7) {
        return intl.formatMessage(sessionDateDisplayMessages.aWeekAgo);
      }
      if (fallbackDayOfWeek) {
        return dayOfWeek;
      }
      return formatDateWithUserPreference(sessionDay, dateFormat, intl);
    },
    [intl, dateFormat]
  );

  const displayFormattedDate = useCallback(
    (
      sessionDate?: string | null,
      isUtc: boolean = false,
      options?: DisplayFormattedDateOptions
    ): string | undefined => {
      if (!sessionDate) {
        return undefined;
      }

      const sessionDay = parseTimeStamp(sessionDate, isUtc);

      if (options?.calendarTimeZone === 'UTC') {
        // Not `dateStyle: 'medium'`: in ja it zero-pads (2026/03/20) instead of 2026年3月20日.
        return intl.formatDate(sessionDay, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        });
      }

      const sessionDayStartOfDay = startOfDay(sessionDay);
      return formatDateWithUserPreference(sessionDayStartOfDay, dateFormat, intl);
    },
    [dateFormat, intl]
  );

  const displaySessionTime = useCallback(
    (sessionTime?: string | null, isUtc: boolean = false) => {
      const tzTime = parseTimeStamp(sessionTime, isUtc);
      return formatSessionTimeOfDay(tzTime, intl);
    },
    [intl]
  );

  const displayDateTime = useCallback(
    (sessionTime?: string | null, isUtc: boolean = false) => {
      const tzTime = parseTimeStamp(sessionTime, isUtc);
      if (isEnglishLocale(intl.locale)) {
        return format(tzTime, `${dateFormat} h:mma`);
      }
      return `${formatDateWithUserPreference(tzTime, dateFormat, intl)} ${formatSessionTimeOfDay(tzTime, intl)}`;
    },
    [dateFormat, intl]
  );

  return {
    displayFormattedDate,
    displayDayRelative,
    displaySessionTime,
    displayDateTime,
  };
};
